import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MessageCircle, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { POSTS, CATEGORIES, publishedPosts } from "@/lib/blogPosts";
import { buildWaLink, GENERAL_MESSAGE, trackWhatsApp } from "@/lib/services";

export default function BlogPage() {
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");

  const published = publishedPosts();
  const featured = published.find((p) => p.featured) || published[0];

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return published.filter((p) => {
      if (p.slug === featured?.slug) return false;
      if (cat !== "All" && p.category !== cat) return false;
      if (!s) return true;
      return (p.title + " " + p.lead).toLowerCase().includes(s);
    });
  }, [published, featured, cat, q]);

  return (
    <main data-testid="blog-page" className="pt-32 md:pt-40">
      <section className="container-x pb-8">
        <h1 className="text-[36px] md:text-[52px] font-extrabold text-navy leading-[1.08]">
          Health Blog — practical home care advice from nurses
        </h1>
        <p className="mt-4 text-ink/85 text-[18px] max-w-2xl">Simple, honest guides written for worried families — by the nurses of Velixa Health.</p>
      </section>

      {/* Featured */}
      {featured && (
        <section className="container-x">
          <Link data-testid="blog-featured" to={`/blog/${featured.slug}`} className="grid grid-cols-12 gap-6 bg-white rounded-3xl border border-navy/10 shadow-soft overflow-hidden hover-care">
            <div className="col-span-12 lg:col-span-6 aspect-[16/10] lg:aspect-auto bg-soft-blue">
              <img src={featured.image} alt={featured.alt} className="w-full h-full object-cover" />
            </div>
            <div className="col-span-12 lg:col-span-6 p-7 md:p-10 flex flex-col justify-center">
              <span className="inline-flex w-fit px-3 py-1 rounded-full bg-mint text-care text-[12px] font-bold uppercase tracking-widest">{featured.category}</span>
              <h2 className="mt-4 text-[26px] md:text-[34px] font-extrabold text-navy leading-[1.12]">{featured.title}</h2>
              <p className="mt-3 text-ink/80 text-[16px]">{featured.lead.slice(0, 160)}…</p>
              <div className="mt-5 flex items-center gap-3 text-[13.5px] text-ink/60">
                <Clock className="w-4 h-4" /> {featured.readMins} min read · {featured.date}
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* Filters */}
      <section className="container-x mt-10 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/40" />
          <Input data-testid="blog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles…" className="pl-9 h-12 rounded-full bg-white border-navy/15" />
        </div>
        <div className="flex flex-wrap gap-2">
          {["All", ...CATEGORIES].map((c) => (
            <button
              key={c}
              data-testid={`blog-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setCat(c)}
              className={`px-4 py-2 rounded-full text-[13.5px] font-semibold border-2 transition-colors ${cat === c ? "bg-navy text-white border-navy" : "bg-white text-navy border-navy/15 hover:border-care"}`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Grid + CTA */}
      <section className="container-x mt-8 pb-20 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {filtered.map((p) => (
            <Link key={p.slug} data-testid={`blog-card-${p.slug}`} to={`/blog/${p.slug}`} className="bg-white rounded-2xl border border-navy/10 overflow-hidden hover-care flex flex-col">
              <div className="aspect-[16/10] bg-soft-blue">
                <img src={p.image} alt={p.alt} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <span className="inline-flex w-fit px-2.5 py-1 rounded-full bg-mint text-care text-[11px] font-bold uppercase tracking-widest">{p.category}</span>
                <h3 className="mt-3 text-[18px] font-bold text-navy leading-snug">{p.title}</h3>
                <p className="mt-2 text-[14.5px] text-ink/75 line-clamp-2">{p.lead}</p>
                <div className="mt-4 text-[12.5px] text-ink/55">{p.readMins} min read</div>
              </div>
            </Link>
          ))}
          {filtered.length === 0 && <p data-testid="blog-empty" className="text-ink/60 py-10">No articles match your search.</p>}
        </div>

        <aside className="col-span-12 lg:col-span-4">
          <div className="lg:sticky lg:top-32 bg-navy text-white rounded-3xl p-7 shadow-card">
            <h3 className="text-[22px] font-extrabold text-white">Need a nurse at home?</h3>
            <p className="mt-2 text-white/85 text-[15px]">Talk to us on WhatsApp — available 24/7 across Bangalore. We'll help you decide what your family needs.</p>
            <a data-testid="blog-side-cta" href={buildWaLink(GENERAL_MESSAGE)} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("Blog Sidebar")} className="btn-wa mt-5 w-full">
              <MessageCircle className="w-5 h-5" /> WhatsApp Us
            </a>
          </div>
        </aside>
      </section>
    </main>
  );
}
